// Mirrors the backend's ConstraintOverlap.java: a duplicate hensyn is a live constraint for the
// same (employee/citizen, target) pair whose validity period overlaps the one being saved. This
// module answers "which targets are already taken" so the UI can hide them from the dropdown
// before a save is even attempted, rather than relying solely on the backend's 409 response.
import { Constraint } from "../types/constraint";

/**
 * The date picker (`DateCalendar.tsx`) emits a full ISO timestamp, e.g.
 * "2026-02-01T12:00:00.000+01:00", while the API returns a bare "yyyy-MM-dd". Compared as raw
 * strings the timestamp sorts after the plain date, so two hensyn meeting on a single shared day
 * would not be seen to overlap. Slicing to the date fixes it.
 */
const toIsoDate = (value?: string): string | undefined =>
  value ? value.slice(0, 10) : undefined;

// A missing bound is an open bound, so it can never make a period fail to overlap.
const isNotAfter = (start?: string, end?: string): boolean =>
  !start || !end || start <= end;

export const doPeriodsOverlap = (
  startA?: string,
  endA?: string,
  startB?: string,
  endB?: string,
): boolean => {
  const a1 = toIsoDate(startA);
  const a2 = toIsoDate(endA);
  const b1 = toIsoDate(startB);
  const b2 = toIsoDate(endB);
  return isNotAfter(a1, b2) && isNotAfter(b1, a2);
};

/**
 * Scoped by isCategoryConstraint because category ids and citizen ids are independent number
 * sequences and can collide — comparing across kinds could hide a legitimate target just
 * because a constraint of the other kind happens to share its numeric id.
 */
export const getTakenTargetIds = (
  constraints: Constraint[],
  isCategoryConstraint: boolean,
  startDate?: string,
  endDate?: string,
): number[] =>
  constraints
    .filter(
      (constraint) => constraint.isCategoryConstraint === isCategoryConstraint,
    )
    .filter((constraint) =>
      doPeriodsOverlap(
        constraint.startDate,
        constraint.endDate,
        startDate,
        endDate,
      ),
    )
    .map((constraint) => constraint.targetId);

/**
 * Kept separate from getTakenTargetIds rather than calling it with isCategoryConstraint: true —
 * citizen hensyn carry no validity period, so any existing pair is already a duplicate, AND the
 * backend's ConstraintDto(ServiceClientRelationToCategory, ...) constructor never sets
 * isCategoryConstraint, so it arrives null on the wire. Filtering on that flag here would match
 * nothing.
 */
export const getTakenCategoryIdsForCitizen = (
  constraints: Constraint[] = [],
): number[] => constraints.map((constraint) => constraint.targetId);
